import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  UserPlus,
  MessageSquare,
  AtSign,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Rocket,
  Mail,
  UserCheck,
  Share2,
  Bell,
  CheckCheck,
} from 'lucide-react';
import {
  notificationsApi,
  Notification,
  WorkspaceInviteMetadata,
} from '../../api/endpoints/notificationsApi';
import { tasksApi } from '../../api/endpoints/tasksApi';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';

const isWorkspaceInvite = (n: Notification) =>
  n.type?.toUpperCase() === 'WORKSPACE_INVITE' || n.type?.toLowerCase() === 'workspace_invite';

const parseNotificationDate = (dateString: string | Date) => {
  if (!dateString) return new Date();
  const date = new Date(dateString);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
};

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);

  useEffect(() => {
    loadUnreadCount();
    loadNotifications();

    // Poll every 30 seconds for new notifications
    const interval = setInterval(() => {
      loadUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const data = await notificationsApi.getUnreadCount();
      setUnreadCount(data.count);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationsApi.getNotifications({ limit: 20 });
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationsApi.markAsRead(notificationId);
      loadNotifications();
      loadUnreadCount();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleAcceptInvite = (notification: Notification) => {
    const metadata = notification.metadata as WorkspaceInviteMetadata | undefined;
    if (!metadata?.token) {
      console.error('Workspace invite notification is missing its token', notification);
      return;
    }
    notificationsApi.markAsRead(notification.id).catch(() => {});
    handleClose();
    navigate(`/invite/${metadata.token}`);
  };

  const handleDeclineInvite = async (notification: Notification) => {
    await handleMarkAsRead(notification.id);
  };

  const handleNotificationClick = async (notification: Notification) => {
    handleMarkAsRead(notification.id);

    const taskId = (notification.metadata as any)?.taskId;
    if (taskId) {
      handleClose();
      try {
        const response = await tasksApi.getTask(taskId);
        if (response.task && response.task.boardId) {
          navigate(`/boards/${response.task.boardId}?taskId=${taskId}`);
        }
      } catch (error) {
        console.error('Failed to load task for notification:', error);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      loadNotifications();
      loadUnreadCount();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const renderNotificationTypeMeta = (type: string) => {
    const normalized = (type || '').toUpperCase();

    switch (normalized) {
      case 'TASK_ASSIGNED':
        return {
          icon: <UserPlus size={16} color="#0073ea" />,
          bgColor: 'rgba(0, 115, 234, 0.12)',
          label: 'Task Assigned',
        };
      case 'TASK_COMMENT':
        return {
          icon: <MessageSquare size={16} color="#9b59b6" />,
          bgColor: 'rgba(155, 89, 182, 0.12)',
          label: 'Comment',
        };
      case 'TASK_MENTION':
        return {
          icon: <AtSign size={16} color="#6366f1" />,
          bgColor: 'rgba(99, 102, 241, 0.12)',
          label: 'Mention',
        };
      case 'TASK_DUE_SOON':
        return {
          icon: <Clock size={16} color="#f39c12" />,
          bgColor: 'rgba(243, 156, 18, 0.12)',
          label: 'Due Soon',
        };
      case 'TASK_OVERDUE':
        return {
          icon: <AlertTriangle size={16} color="#e74c3c" />,
          bgColor: 'rgba(231, 76, 60, 0.12)',
          label: 'Overdue',
        };
      case 'TASK_COMPLETED':
        return {
          icon: <CheckCircle2 size={16} color="#22c58c" />,
          bgColor: 'rgba(34, 197, 140, 0.12)',
          label: 'Completed',
        };
      case 'SPRINT_STARTED':
        return {
          icon: <Rocket size={16} color="#8b7fd6" />,
          bgColor: 'rgba(139, 127, 214, 0.12)',
          label: 'Sprint Started',
        };
      case 'WORKSPACE_INVITE':
        return {
          icon: <Mail size={16} color="#00bcd4" />,
          bgColor: 'rgba(0, 188, 212, 0.12)',
          label: 'Invitation',
        };
      case 'MEMBER_JOINED':
        return {
          icon: <UserCheck size={16} color="#27ae60" />,
          bgColor: 'rgba(39, 174, 96, 0.12)',
          label: 'Member Joined',
        };
      case 'BOARD_SHARED':
        return {
          icon: <Share2 size={16} color="#0073ea" />,
          bgColor: 'rgba(0, 115, 234, 0.12)',
          label: 'Board Shared',
        };
      default:
        return {
          icon: <Bell size={16} color="#64748b" />,
          bgColor: 'rgba(100, 116, 139, 0.12)',
          label: 'Notification',
        };
    }
  };

  const filteredNotifications = useMemo(() => {
    if (activeTab === 1) return notifications.filter((n) => !n.isRead);
    if (activeTab === 2) return notifications.filter((n) => isWorkspaceInvite(n));
    return notifications;
  }, [notifications, activeTab]);

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={handleClick} sx={{ p: 1 }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon sx={{ color: '#475569', fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 520,
            width: 420,
            maxWidth: '92vw',
            borderRadius: '12px',
            boxShadow: '0 12px 36px rgba(0,0,0,0.15)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem', color: '#0f172a' }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Box
                component="span"
                sx={{
                  bgcolor: '#0073ea',
                  color: 'white',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  px: 1,
                  py: 0.2,
                  borderRadius: 10,
                }}
              >
                {unreadCount} unread
              </Box>
            )}
          </Box>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<CheckCheck size={14} />}
              onClick={handleMarkAllAsRead}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.775rem', color: '#0073ea' }}
            >
              Mark all as read
            </Button>
          )}
        </Box>

        {/* Category Tabs */}
        <Box sx={{ borderBottom: '1px solid #e2e8f0', px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            sx={{
              minHeight: 38,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                minHeight: 38,
                py: 0.5,
                px: 1.5,
                color: '#64748b',
                '&.Mui-selected': { color: '#0073ea' },
              },
              '& .MuiTabs-indicator': { backgroundColor: '#0073ea', height: 3 },
            }}
          >
            <Tab label="All" />
            <Tab label={`Unread (${notifications.filter((n) => !n.isRead).length})`} />
            <Tab label="Invites" />
          </Tabs>
        </Box>

        {/* Notifications List */}
        <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={26} />
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Box sx={{ px: 3, py: 5, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'rgba(99,102,241,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                }}
              >
                <Bell size={22} color="#6366f1" />
              </Box>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                You're all caught up!
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                No notifications right now.
              </Typography>
            </Box>
          ) : (
            filteredNotifications.map((notification) => {
              const invite = isWorkspaceInvite(notification);
              const meta = renderNotificationTypeMeta(notification.type);

              return (
                <MenuItem
                  key={notification.id}
                  onClick={invite ? undefined : () => handleNotificationClick(notification)}
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid #f1f5f9',
                    backgroundColor: notification.isRead ? 'transparent' : 'rgba(0,115,234,0.03)',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    whiteSpace: 'normal',
                    cursor: invite ? 'default' : 'pointer',
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: invite
                        ? notification.isRead
                          ? 'transparent'
                          : 'rgba(0,115,234,0.03)'
                        : 'rgba(0,115,234,0.06)',
                    },
                  }}
                  disableRipple={invite}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1.5 }}>
                    {/* Icon Avatar Box */}
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: meta.bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      {meta.icon}
                    </Box>

                    {/* Notification Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: notification.isRead ? 600 : 700,
                            fontSize: '0.85rem',
                            color: '#0f172a',
                          }}
                        >
                          {notification.title}
                        </Typography>

                        {!notification.isRead && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#0073ea',
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.8rem',
                          color: '#475569',
                          mt: 0.25,
                          lineHeight: 1.4,
                        }}
                      >
                        {notification.message}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{ fontSize: '0.725rem', color: '#94a3b8', mt: 0.5, display: 'block' }}
                      >
                        {formatDistanceToNow(parseNotificationDate(notification.createdAt), { addSuffix: true })}
                      </Typography>

                      {/* Workspace Invite Actions */}
                      {invite && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptInvite(notification);
                            }}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              bgcolor: '#0073ea',
                              borderRadius: '6px',
                              px: 2,
                              py: 0.4,
                            }}
                          >
                            Accept
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeclineInvite(notification);
                            }}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              color: '#64748b',
                              borderColor: '#cbd5e1',
                              borderRadius: '6px',
                              px: 2,
                              py: 0.4,
                            }}
                          >
                            Decline
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              );
            })
          )}
        </Box>
      </Menu>
    </>
  );
};

export default NotificationBell;
